import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  filter, map, NEVER, Observable, of, shareReplay, Subscription, take,
} from 'rxjs';
import { SlideInResponse } from 'app/modules/slide-ins/slide-in.interface';

/**
 * Excludes `undefined` from T while preserving other falsy types (`null`, `false`, `0`, `''`).
 * Used to narrow the response type after filtering out cancellations (undefined).
 */
type NonUndefined<T> = Exclude<T, undefined>;

/**
 * Extends Observable<SlideInResponse<R>> with convenience methods
 * for common slide-in patterns.
 *
 * Since it extends Observable, existing .pipe() and .subscribe() callers
 * continue to work unchanged, while new callers can use .onSuccess().
 *
 * Internally the source is replayed (shareReplay(1)) so that subscribing
 * via both the Observable interface and the convenience methods does not
 * cause duplicate side-effects on the upstream.
 */
export class SlideInResult<R> extends Observable<SlideInResponse<R>> {
  private shared$: Observable<SlideInResponse<R>>;

  /**
   * Observable that emits only the unwrapped response value
   * when the slide-in was closed successfully (response !== undefined).
   * Useful in switchMap chains where .onSuccess() cannot be used.
   *
   * Unlike .onSuccess(), this does NOT auto-unsubscribe — callers must
   * add their own takeUntilDestroyed() or other teardown.
   */
  readonly success$: Observable<NonUndefined<R>>;

  /**
   * Observable that emits `void` when the slide-in was cancelled (response === undefined).
   * Useful in switchMap/merge chains where .onCancel() cannot be used.
   *
   * Unlike .onCancel(), this does NOT auto-unsubscribe — callers must
   * add their own takeUntilDestroyed() or other teardown.
   */
  readonly cancel$: Observable<void>;

  constructor(source$: Observable<SlideInResponse<R>>) {
    // take(1) ensures the source completes after a single emission, preventing
    // memory leaks if a caller constructs a SlideInResult from a non-completing source.
    // refCount: false so late subscribers (e.g. onCancel after onSuccess) still
    // receive the replayed value even if earlier subscribers have already unsubscribed.
    const shared$ = source$.pipe(take(1), shareReplay({ bufferSize: 1, refCount: false }));
    super((subscriber) => shared$.subscribe(subscriber));
    this.shared$ = shared$;
    this.success$ = this.shared$.pipe(
      filter((result): result is SlideInResponse<R> & { response: NonUndefined<R> } => result.response !== undefined),
      map((result) => result.response),
    );
    this.cancel$ = this.shared$.pipe(
      filter((result) => result.response === undefined),
      map((): void => {}),
    );
  }

  /**
   * Creates a SlideInResult that never emits and never completes.
   * Useful in tests where the slide-in result is irrelevant:
   *   mockProvider(SlideIn, { open: jest.fn(() => SlideInResult.empty()) })
   *
   * Note: since this never emits or completes, .onSuccess / .onCancel / .onClose
   * callbacks will NOT fire. Use .cancel() or .success() if you need them to.
   */
  static empty<T = unknown>(): SlideInResult<T> {
    return new SlideInResult<T>(NEVER);
  }

  /**
   * Creates a SlideInResult that emits a cancel (undefined response). Useful in tests:
   *   mockProvider(SlideIn, { open: jest.fn(() => SlideInResult.cancel()) })
   */
  static cancel<T = unknown>(): SlideInResult<T> {
    return new SlideInResult<T>(of({ response: undefined }));
  }

  /**
   * Creates a SlideInResult that emits a single successful response. Useful in tests:
   *   mockProvider(SlideIn, { open: jest.fn(() => SlideInResult.success(true)) })
   */
  static success<T>(value: T): SlideInResult<T> {
    return new SlideInResult<T>(of({ response: value }));
  }

  /**
   * Subscribes to successful slide-in closes and invokes the callback.
   * Automatically unsubscribes when the caller's DestroyRef is destroyed.
   *
   * Replaces the common pattern:
   *   .pipe(filter(r => r.response !== undefined), takeUntilDestroyed(destroyRef)).subscribe(...)
   *
   * Only `undefined` is treated as cancellation — falsy values like `null`, `false`, `0`, `''`
   * are treated as success.
   */
  onSuccess(callback: (response: NonUndefined<R>) => void, destroyRef: DestroyRef): Subscription {
    return this.success$.pipe(
      takeUntilDestroyed(destroyRef),
    ).subscribe((response) => callback(response));
  }

  /**
   * Subscribes to slide-in closes where the user cancelled (response === undefined)
   * and invokes the callback. Automatically unsubscribes when destroyed.
   */
  onCancel(callback: () => void, destroyRef: DestroyRef): Subscription {
    return this.cancel$.pipe(
      takeUntilDestroyed(destroyRef),
    ).subscribe(() => callback());
  }

  /**
   * Subscribes to any slide-in close (success or cancel) and invokes the callback.
   * Automatically unsubscribes when the caller's DestroyRef is destroyed.
   */
  onClose(callback: () => void, destroyRef: DestroyRef): Subscription {
    return this.shared$.pipe(
      takeUntilDestroyed(destroyRef),
    ).subscribe(() => callback());
  }
}
